import axios from 'axios';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { authLog, authTrail, fingerprint } from 'src/sentry';

import { requestFromApp } from 'src/utils/post-to-app';

import { useAuth } from './use-auth';
import { useUser } from './use-user';

const noAuthorization = [
  'auth/login/',
  'auth/registration/',
  'v1/projects/',
  'auth/password/reset/',
  'auth/password/reset/confirm/',
  'login/bp-scan/',
  'login/magic/',
  'login/request-magic-link/',
  'login/request-otp/',
  'login/verify-otp/',
];

const useAxios = () => {
  const { auth, setAuth, clearedToken } = useAuth();
  const { lang } = useUser();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlFlow = queryParams.get('flow');

  return useMemo(() => {
    const apiInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });

    apiInstance.interceptors.request.use(
      (config) => {
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Token ${auth}`;
        }
        if (noAuthorization.includes(config.url)) {
          config.headers.Authorization = null;
        }
        config.headers['Accept-Language'] = lang;
        authTrail('request', { url: config.url, method: config.method, hasAuth: !!auth });
        return config;
      },
      (error) => Promise.reject(error)
    );

    apiInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const url = error.config?.url;

        if (status === 401) {
          const failedToken = error.config?.headers?.Authorization?.slice('Token '.length);
          authLog('401', { url, method: error.config?.method, status: 401, failedTokenFp: fingerprint(failedToken) });

          // Ask native for a token and classify the outcome into a named string so the
          // recovery vs. silent-logout decision is searchable in Sentry.
          let outcome = 'no_webview';
          let token;
          if (window.ReactNativeWebView) {
            ({ token } = await requestFromApp('GET_AUTH_TOKEN'));
            if (!token) outcome = 'missing_or_timeout';
            else if (token === failedToken) outcome = 'same_as_failed';
            else if (token === clearedToken.current) outcome = 'cleared_token_match';
            else outcome = 'recovered';
            authTrail('native token outcome', { outcome, nativeTokenFp: fingerprint(token) });
          }

          if (outcome === 'recovered') {
            setAuth(token);
            return Promise.reject(error);
          }

          authLog('auth:clear via 401', { url, reason: outcome });
          setAuth(null);
        }
        return Promise.reject(error);
      }
    );

    apiInstance.event = async (event, flow) => {
      // event can be either a string or an array of strings
      const events = Array.isArray(event) ? event : [event];
      await events.reduce(async (previousPromise, e) => {
        await previousPromise;

        return apiInstance.post(`v1/events/`, {
          flow: urlFlow || flow || 'additional_onboarding_v4',
          event: e,
        });
      }, Promise.resolve());
    };

    return apiInstance;
  }, [auth, lang, setAuth, clearedToken, urlFlow]);
};

export default useAxios;
